import Link from "next/link";

import { cn } from "@/lib/utils";

type PaginationProps = {
  page: number;
  pages: number;
  pathFor: (page: number) => string;
};

const pageWindow = (page: number, pages: number): (number | "gap")[] => {
  const wanted = new Set<number>([1, pages]);
  for (let candidate = page - 2; candidate <= page + 2; candidate++) {
    if (candidate >= 1 && candidate <= pages) wanted.add(candidate);
  }

  const items: (number | "gap")[] = [];
  let previous = 0;
  for (const value of Array.from(wanted).sort((a, b) => a - b)) {
    if (previous && value - previous > 1) items.push("gap");
    items.push(value);
    previous = value;
  }
  return items;
};

const stepClass =
  "rounded-full border border-gray-200 px-3.5 py-1.5 text-sm text-gray-900 transition-colors hover:bg-gray-100";
const stepDisabledClass =
  "rounded-full border border-gray-100 px-3.5 py-1.5 text-sm text-gray-300";

export const Pagination = ({ page, pages, pathFor }: PaginationProps) => {
  if (pages <= 1) return null;

  return (
    <nav aria-label="Pagination" className="mt-12 flex justify-center">
      <ul className="flex flex-wrap items-center justify-center gap-2">
        <li>
          {page > 1 ? (
            <Link href={pathFor(page - 1)} rel="prev" className={stepClass}>
              Previous
            </Link>
          ) : (
            <span className={stepDisabledClass}>Previous</span>
          )}
        </li>

        {pageWindow(page, pages).map((item, index) =>
          item === "gap" ? (
            <li key={`gap-${index}`} aria-hidden="true" className="px-1 text-sm text-gray-400">
              ...
            </li>
          ) : (
            <li key={item}>
              {item === page ? (
                <span
                  aria-current="page"
                  className="rounded-full bg-gray-900 px-3.5 py-1.5 text-sm text-white"
                >
                  {item}
                </span>
              ) : (
                <Link
                  href={pathFor(item)}
                  aria-label={`Page ${item}`}
                  className={cn(stepClass, "min-w-[2.5rem] text-center")}
                >
                  {item}
                </Link>
              )}
            </li>
          ),
        )}

        <li>
          {page < pages ? (
            <Link href={pathFor(page + 1)} rel="next" className={stepClass}>
              Next
            </Link>
          ) : (
            <span className={stepDisabledClass}>Next</span>
          )}
        </li>
      </ul>
    </nav>
  );
};
